const en = {
  // Common
  common: {
    loading: 'Loading...',
    loadingMore: 'Loading more...',
    reachedEnd: 'You reached the end.',
    seeAll: 'See all',
    viewAll: 'View all',
    retry: 'Retry',
    clearFilters: 'Clear filters',
    clearFilter: 'Clear filter',
    all: 'All',
    premium: 'Premium',
    series: 'Series',
    views: 'views',
    eps: 'eps',
    cancel: 'Cancel',
  },

  // Navigation
  nav: {
    drama: 'Drama',
    search: 'Search',
    premium: 'Premium',
  },

  // Header
  header: {
    notifications: 'Notifications',
    profile: 'Profile',
  },

  // Home Page
  home: {
    genres: 'Genres',
    trendingNow: 'Trending Now',
    tagPicksFallback: 'Picks For You',
    latest: 'Latest',
    latestSubtitle: 'Infinite scroll with 20 items per fetch.',
    loadedOf: 'Loaded {{current}} of {{total}}',
    play: 'Play',
    details: 'Details',
    noLatestBoxes: 'No latest boxes found.',
  },

  // Search Page
  search: {
    placeholder: 'Search movies, genres...',
    noResults: 'No results found',
    tryDifferent: 'Try searching with different keywords',
    browseCollection: 'Browse our collection or try a different category',
    results: '{{count}} result{{plural}}',
    zeroResults: '0 results',
    ofTotal: ' of {{total}}',
    noBoxesFound: 'No boxes found for the current filters.',
  },

  // Profile Page
  profile: {
    title: 'Profile',
    episodesWatched: 'Episodes Watched',
    coinsBalance: 'Coins Balance',
    watchlist: 'Watchlist',
    account: 'Account',
    userInformation: 'User Information',
    userInfoDesc: 'Name, email, password',
    transactionHistory: 'Transaction History',
    transactionDesc: 'Purchases & coin usage',
    subscriptionPlans: 'Subscription & Plans',
    subscriptionDesc: 'Manage your plan',
    signOut: 'Sign Out',
    footer: 'MMBox v1.0.0 • Terms & Privacy',
    termsAndPrivacy: 'Terms & Privacy',
    language: 'Language',
    languageDesc: 'Change display language',
    english: 'English',
    burmese: 'Myanmar',
    telegramUser: 'Telegram User',
  },

  // Purchase Page
  purchase: {
    title: 'My Purchases',
    items: '{{count}} items',
    noPurchases: 'No purchases yet',
    failedToLoad: 'Failed to load purchases',
  },

  // Notification Page
  notification: {
    title: 'Notifications',
    unreadOnly: 'Unread Only',
    all: 'All',
    loading: 'Loading notifications...',
    failedToLoad: 'Failed to load notifications.',
    noUnread: 'No unread notifications',
    noNotifications: 'No notifications yet',
    loadMore: 'Load more',
    justNow: 'Just now',
    minAgo: '{{count}}m ago',
    hourAgo: '{{count}}h ago',
    dayAgo: '{{count}}d ago',
  },

  // Transaction Page
  transaction: {
    title: 'Transaction History',
    count: '{{count}} transactions',
    noTransactions: 'No transactions yet',
    failedToLoad: 'Failed to load transactions',
    purchase: 'Purchase',
    subscription: 'Subscription',
    refund: 'Refund',
    topUp: 'Top Up',
    boxPurchase: 'Box Purchase',
    completed: 'Completed',
    active: 'Active',
    pending: 'Pending',
    failed: 'Failed',
    cancelled: 'Cancelled',
    filterByStatus: 'Filter by status',
    allStatuses: 'All statuses',
    transactionLabel: 'Transaction',
  },

  // Movie Detail
  movieDetail: {
    invalidId: 'Invalid movie id.',
    failedToLoad: 'Failed to load movie detail.',
    epsFree: '{{count}} eps free',
    allEpisodes: 'All {{count}} episodes',
    seeMore: 'See more',
    playFree: 'Play Free Episodes Now',
    sending: 'Sending...',
    signInToUnlock: 'Sign In to unlock',
    unlockEpisodes: 'Unlock {{count}} more episodes',
    signingIn: 'Signing in...',
    youOwnThis: 'You own this',
    watchNow: 'Watch Now',
    processing: 'Processing...',
    playbackFailed: 'Playback failed: {{error}}',
  },

  // Episode Select
  episodeSelect: {
    title: 'Select Episodes',
    subtitle: 'Choose episodes to send to Telegram',
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    loading: 'Loading episodes...',
    failedToLoad: 'Failed to load episodes.',
    ep: 'Ep',
    sending: 'Sending...',
    sendToTelegram: 'Send {{count}} episode{{plural}} to Telegram',
    episodesAvailable: '{{count}} episodes available',
    selectedCount: '{{selected}} of {{total}} selected',
  },

  // Watch Modal
  watchModal: {
    loadingVideo: 'Loading video...',
    unableToPlay: 'Unable to Play',
    tryAgain: 'Try Again',
  },

  // Not Found Page
  notFound: {
    title: 'Page Not Found',
    description:
      "Oops! The page you're looking for doesn't exist or has been moved.",
    goHome: 'Go Home',
    browseContent: 'Browse Content',
  },

  // Vertical Box List
  boxList: {
    noDescription: 'No description available.',
  },

  // Premium Modal
  premium: {
    premiumContent: 'Premium Content',
    episodeInfo: '{{total}} episodes total ({{free}} free, {{locked}} locked)',
    totalPrice: 'Total Price',
    unlockAllEpisodes: 'Unlock all episodes with one purchase',
    buyNow: 'Buy Now',
    processing: 'Processing...',
  },

  // Routes fallback
  routes: {
    loading: 'Loading...',
  },

  // Terms and Conditions
  terms: {
    title: 'Terms & Conditions',
    back: 'Go back',
    updated: 'Last updated: April 2026',
    intro:
      'By using MMBox, you agree to follow these terms. Please read them carefully before continuing to use the app.',
    sec1Title: '1. Account and Access',
    sec1Body:
      'You are responsible for your account access and activity. Premium access and purchased content are tied to your account and may not be shared in unauthorized ways.',
    sec2Title: '2. Content Usage',
    sec2Body:
      'All videos and assets in MMBox are for personal viewing only. Downloading, redistributing, or commercial use without permission is prohibited.',
    sec3Title: '3. Purchases and Refunds',
    sec3Body:
      'Payments for premium content are processed securely through supported channels. Completed purchases are generally non-refundable except where required by applicable law.',
    sec4Title: '4. Updates and Changes',
    sec4Body:
      'We may update features, pricing, or these terms from time to time. Continued use of MMBox after changes means you accept the revised terms.',
  },
} as const;

export default en;

export type TranslationKeys = Record<string, unknown>;
